import { Link } from "react-router-dom";

const BENEFITS = [
  {
    icon: "ri-checkbox-circle-line",
    title: "Over 25 Years of Expertise",
    detail:
      "Decades of hands-on experience evaluating used cars means we know exactly what to look for — and where sellers try to hide problems.",
  },
  {
    icon: "ri-car-washing-line",
    title: "Inspection Specialists Only",
    detail:
      "Pre-purchase inspections are all we do. We have zero repair business — which means zero conflict of interest. Our only goal is giving you accurate, complete information.",
  },
  {
    icon: "ri-user-voice-line",
    title: "Your Actual Inspector Calls You",
    detail:
      "Immediately after the inspection, the technician who examined your car calls you personally to review findings. No call center, no middlemen — only at Lemon Protector.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "We Travel to the Vehicle",
    detail:
      "We come on-site to wherever the car is located across NY, NJ, and CT. You never need to move the vehicle or coordinate with the seller — we handle everything.",
  },
  {
    icon: "ri-file-damage-line",
    title: "We Find What CARFAX Misses",
    detail:
      "CARFAX only shows officially reported history. Our 450-point physical inspection uncovers unreported accidents, flood damage, and hidden mechanical problems that no database can reveal.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "One Inspection Pays for Itself",
    detail:
      "A single cracked engine block can cost $6,000+ to repair. One $139 inspection gives you the leverage to negotiate, walk away from a bad deal, or buy with total confidence.",
  },
];

export default function WhyChooseUs() {
  return (
    <section className="bg-brand-dark py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-brand-gold/15">
          {/* Left image panel */}
          <div className="relative min-h-[400px] lg:min-h-[680px] lp-img-zoom overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/f30138e11856ceee2e6d9d2d68473282.jpg"
              alt="Lemon Protector — expert automotive inspector with 25 years experience"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            {/* Gradient fade to right */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-brand-dark/90 hidden lg:block" />
            {/* Bottom fade for mobile */}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/95 via-brand-dark/40 to-transparent lg:hidden" />

            {/* Floating experience badge */}
            <div className="absolute top-8 right-8 bg-brand-black/80 backdrop-blur-md border border-brand-gold/35 rounded-2xl px-5 py-4 text-center">
              <p className="font-serif text-4xl font-bold text-brand-gold leading-none">25<span className="text-2xl">+</span></p>
              <p className="font-sans text-xs text-brand-gray-light mt-1 leading-tight whitespace-nowrap">Years Inspecting<br />Used Cars</p>
            </div>

            {/* BBB badge bottom */}
            <div className="absolute bottom-8 left-8">
              <a
                href="http://www.bbb.org/new-york-city/business-reviews/auto-diagnostic-service/lemon-protector-in-plainview-ny-8459/"
                rel="nofollow"
                target="_blank"
              >
                <img
                  src="https://lemonprotector.com/images/home/blue-seal-63-134-lemon-protector-8459.png"
                  alt="BBB Accredited — Lemon Protector"
                  className="w-16 object-contain"
                />
              </a>
            </div>

            {/* Price callout */}
            <div className="absolute bottom-8 right-8 bg-brand-gold rounded-2xl px-5 py-3 text-center hidden lg:block">
              <p className="font-serif text-xl font-bold text-brand-black leading-none">$139</p>
              <p className="font-sans text-xs text-brand-black/70 mt-0.5 whitespace-nowrap">Starting Price</p>
            </div>
          </div>

          {/* Right content */}
          <div className="bg-brand-dark lg:bg-brand-dark p-10 lg:p-14 flex flex-col justify-center">
            <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-brand-gold" />
              Why Lemon Protector
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-brand-white leading-tight mb-3">
              Built for Buyers.<br />
              <span className="text-brand-gold italic">Not Repair Shops.</span>
            </h2>
            <p className="font-sans text-sm text-brand-gray mb-8 leading-relaxed max-w-md">
              We don&apos;t fix cars. We inspect them — and we tell you everything we find with zero conflicts of interest, so you make the smartest decision possible.
            </p>

            <ul className="flex flex-col gap-5 mb-10">
              {BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0 rounded-xl bg-brand-gold/10 mt-0.5 group-hover:bg-brand-gold/20 transition-colors">
                    <i className={`${b.icon} text-brand-gold text-lg`} />
                  </div>
                  <div>
                    <p className="font-serif text-base font-semibold text-brand-white mb-1">{b.title}</p>
                    <p className="font-sans text-sm text-brand-gray leading-relaxed">{b.detail}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all w-fit whitespace-nowrap cursor-pointer"
              >
                Book an On-Site Inspection
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-brand-gold/30 text-brand-gold font-sans font-medium text-sm rounded-full hover:bg-brand-gold/8 transition-all w-fit whitespace-nowrap cursor-pointer"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
